/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {
    getLocaleByReference,
    getParamsFromPath,
    getDefaultSite,
    getSiteByReference
} from '@salesforce/retail-react-app/app/utils/site-utils'
import {HOME_HREF, urlPartPositions} from '@salesforce/retail-react-app/app/constants'

const LOCALIZABLE_HREF_PATTERN = /^(\/|https?:\/\/|\/\/)/i
const NON_NAVIGABLE_HREF_PATTERN = /^(#|mailto:|tel:|javascript:)/i
const HTML_HREF_ATTR_PATTERN = /(\shref\s*=\s*)(["'])([^"']*)\2/gi

/**
 * Constructs an absolute URL from a given path and an optional application origin.
 *
 * @param {string} path - The relative URL path to be appended to the origin.
 * @param {string} [appOrigin] - The optional application origin (e.g., "https://example.com").
 *                                If not provided, the function will call `getAppOrigin()`.
 * @returns {string} - The fully qualified URL as a string.
 */
export const absoluteUrl = (path, appOrigin) => {
    return new URL(path, appOrigin || getAppOrigin()).toString()
}

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (url, extraParams) => {
    const [pathname, search] = url.split('?')
    const params = new URLSearchParams(search)

    updateSearchParams(params, extraParams)

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '')

    // Generate the newly updated url.
    return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`
}

export const updateSearchParams = (searchParams, newParams) => {
    Object.entries(newParams).forEach(([key, value]) => {
        // 0 is a valid value as for a param
        if (!value && value !== 0) {
            searchParams.delete(key)
        } else {
            searchParams.set(key, value)
        }
    })
}

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
export const buildUrlSet = (url = '', key = '', values = [], extraParams = {}) =>
    values.map((value) => rebuildPathWithParams(url, {[key]: value, ...extraParams}))

/**
 * Returns the href to a category's product list page.
 * Returns the SFCC-provided `c_relativeUrl` (already includes brand segment).
 * Any site/locale prefix already baked into the URL is stripped so the PWA Kit
 * <Link> adds exactly one via `buildUrl` — otherwise SFCC-baked locale segments
 * (e.g. `/ca/d/new/`) double/triple up (`/ca/ca/ca/d/new/`).
 *
 * @param {Object} category - The category object
 * @returns {string|null}
 */
export const categoryUrlBuilder = (category) => {
    const relativeUrl = category?.c_relativeUrl
    if (!relativeUrl) return null
    const {siteRef, localeRef} = getParamsFromPath(relativeUrl)
    let path = relativeUrl
    if (siteRef) path = path.replace(`/${siteRef}`, '')
    if (localeRef) path = path.replace(`/${localeRef}`, '')
    return path
}

/**
 * UGC routes embed a brand segment in the path
 * (`/:brandId([g|d]{1})/ugc/...` — see routes.jsx), which `buildUrl` itself
 * doesn't know about. Shared by the landing tile and the expanded view so the
 * mapping lives in one place.
 */
export const UGC_BRAND_ROUTE_SEGMENT = {dynamite: 'd', garage: 'g'}

export const getUgcBrandSegment = (brandId) => UGC_BRAND_ROUTE_SEGMENT[brandId] || 'd'

/**
 * Internal href to a look's expanded page, carrying the source category id
 * (`cgid`) forward so the expanded breadcrumb and its landing CTAs can resolve.
 *
 * @param {string} brandId
 * @param {string} itemId
 * @param {string} [cgid]
 * @returns {string}
 */
export const buildUgcItemPath = (brandId, itemId, cgid) =>
    `/${getUgcBrandSegment(brandId)}/ugc/item/${itemId}${
        cgid ? `?cgid=${encodeURIComponent(cgid)}` : ''
    }`

/**
 * Internal href to the UGC landing grid, optionally scoped to a category id.
 *
 * @param {string} brandId
 * @param {string} [cgid]
 * @returns {string}
 */
export const buildUgcLandingPath = (brandId, cgid) =>
    `/${getUgcBrandSegment(brandId)}/ugc/${cgid ? `?cgid=${encodeURIComponent(cgid)}` : ''}`

// Href to the UGC landing: the UGC category's own c_relativeUrl (the nav's
// data-driven URL, e.g. /d/styled-by-you), falling back to the static
// /brand/ugc/ route. cgid keeps scoping the grid.
export const buildUgcLandingHref = (ugcCategory, brandId, cgid) => {
    const categoryHref = categoryUrlBuilder(ugcCategory)
    if (!categoryHref) return buildUgcLandingPath(brandId, cgid)
    return `${categoryHref}${cgid ? `?cgid=${encodeURIComponent(cgid)}` : ''}`
}

const productNameToSlug = (productName) =>
    String(productName)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2019]/g, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

/**
 * Given a product and the current locale returns an href to the product detail page.
 *
 * @param {Object} product
 * @returns {string}
 */
export const productUrlBuilder = function (product) {
    if (product?.c_relativeUrl) {
        return encodeURI(product.c_relativeUrl)
    }

    const productId = product?.productId || product?.id
    if (!productId) {
        return null
    }

    const productName = product?.productName || product?.name || productId
    const productNameSlug = productNameToSlug(productName) || productId
    return `/p/${encodeURIComponent(productNameSlug)}/${encodeURIComponent(productId)}.html`
}

/**
 * Builds a search URL with the given query term.
 * The locale prefix is added by PWA Kit's <Link>/useNavigation.
 *
 * @param {string} searchTerm
 * @returns {string}
 */
export const buildSearchUrl = (searchTerm) =>
    `/search?q=${encodeURIComponent(searchTerm)}`


/**
 * Given a search term, constructs a search url.
 * Optional extra params (e.g. searchSource) are forwarded to /api/product-search-ssr and can trigger custom server logic before SCAPI.
 *
 * @param {string} searchTerm
 * @param {Record<string, string>} [extraParams] - Optional query params (e.g. { searchSource: 'promo' })
 * @returns {string}
 */
export const searchUrlBuilder = (searchTerm, extraParams = {}) => {
    const base = '/search?q=' + encodeURIComponent(searchTerm)
    if (!extraParams || Object.keys(extraParams).length === 0) return base
    const params = new URLSearchParams(extraParams)
    return `${base}&${params.toString()}`
}

/**
 * Ensures an internal href carries the current site/locale prefix exactly once.
 *
 * Variation/swatch hrefs are built from mixed sources: some come from SFCC's
 * locale-less `c_relativeUrl`, while others are derived from `location.pathname`
 * (which already includes the locale). Passing either straight to a raw
 * react-router `<Link>` produces inconsistent URLs — the locale-less ones 404.
 *
 * This helper strips any existing site/locale segment and re-applies it through
 * `buildUrl`, so the result always matches the app's URL configuration.
 *
 * @param {string} href - The internal href (may or may not include a locale prefix, may include a query string).
 * @param {function(string): string} buildUrl - `buildUrl` from `useMultiSite`.
 * @returns {string} The href with the current site/locale prefix.
 */
export const localizeHref = (href, buildUrl) => {
    if (!href || typeof href !== 'string' || /^https?:\/\//i.test(href)) return href
    if (typeof buildUrl !== 'function') return href

    try {
        const [rawPath, rawSearch = ''] = href.split('?')
        const search = rawSearch ? `?${rawSearch}` : ''

        const {siteRef, localeRef} = getParamsFromPath(`${rawPath}${search}`)

        let path = rawPath
        if (siteRef) path = path.replace(`/${siteRef}`, '')
        if (localeRef) path = path.replace(`/${localeRef}`, '')
        if (!path.startsWith('/')) path = `/${path}`

        return buildUrl(`${path}${search}`)
    } catch (e) {
        return buildUrl(href)
    }
}

/**
 * Returns a relative URL for a locale short code.
 * Based on your app configuration, this function will replace your current locale shortCode with a new one
 *
 * @param {String} shortCode - The locale short code.
 * @param {function(*, *, *, *=): string} - Generates a site URL from the provided path, site and locale.
 * @param {string[]} opts.disallowParams - URL parameters to remove
 * @param {Object} opts.location - location object to replace the default `window.location`
 * @returns {String} url - The relative URL for the specific locale.
 */
export const getPathWithLocale = (shortCode, buildUrl, opts = {}) => {
    const location = opts.location ? opts.location : window.location
    let {siteRef, localeRef} = getParamsFromPath(`${location.pathname}${location.search}`)
    let {pathname, search} = location

    // sanitize the site from current url if existing
    if (siteRef) {
        pathname = pathname.replace(`/${siteRef}`, '')
        search = search.replace(`site=${siteRef}`, '')
    }
    // sanitize the locale from current url if existing
    if (localeRef) {
        pathname = pathname.replace(`/${localeRef}`, '')
        search = search.replace(`locale=${localeRef}`, '')
    }
    // remove ending any &
    search = search.replace(/&$/, '')

    // Remove query parameters
    const {disallowParams = []} = opts

    let queryString = new URLSearchParams(`${search}`)

    if (disallowParams.length) {
        disallowParams.forEach((param) => {
            queryString.delete(param)
        })
    }

    const site = getSiteByReference(siteRef)
    const locale = getLocaleByReference(site, shortCode)

    // rebuild the url with new locale,
    const newUrl = buildUrl(
        `${pathname}${Array.from(queryString).length !== 0 ? `?${queryString}` : ''}`,
        // By default, as for home page, when the values of site and locale belongs to the default site,
        // they will be not shown in the url just
        site.alias || site.id,
        locale?.alias || locale?.id
    )
    return newUrl
}

/**
 * Generates the URL Template literal (Template string) used to build URLs in the App according
 * the current selected site/locale and the default App URL configuration.
 *
 * @param appConfig Application default configuration.
 * @param siteRef Current selected Site reference. The value can be the Site id or alias.
 * @param localeRef Current selected Locale reference. The value can be the Locale id or alias.
 * @returns {function(*, *, *): string} function providing: path, site and locale generates a URL.
 */
export const createUrlTemplate = (appConfig, siteRef, localeRef) => {
    const {site: siteConfig, locale: localeConfig, showDefaults: showDefaultsConfig} = appConfig.url
    const defaultSite = getDefaultSite()
    const site = getSiteByReference(siteRef)
    const defaultLocale = getLocaleByReference(site, site.l10n.defaultLocale)

    const isDefaultSite =
        defaultSite.id === siteRef || (defaultSite.alias && defaultSite.alias === siteRef)
    const isDefaultLocale =
        defaultLocale.id === localeRef || (defaultLocale.alias && defaultLocale.alias === localeRef)

    const querySite =
        (siteConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultSite)
    const queryLocale =
        (localeConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultLocale)

    const isQuery = querySite || queryLocale

    const pathSite =
        (siteConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultSite)
    const pathLocale =
        (localeConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultLocale)

    return (path, site, locale) => {
        const isHomeWithDefaultSiteAndLocale =
            path === HOME_HREF &&
            (defaultSite.id === site || (defaultSite.alias && defaultSite.alias === site)) &&
            (defaultLocale.id === locale || (defaultLocale.alias && defaultLocale.alias === locale))

        const sitePath = pathSite && site && !isHomeWithDefaultSiteAndLocale ? `/${site}` : ''
        const localePath =
            pathLocale && locale && !isHomeWithDefaultSiteAndLocale ? `/${locale}` : ''

        const hasQuery = isQuery && (site || locale) && !isHomeWithDefaultSiteAndLocale
        let queryString = ''
        if (hasQuery) {
            const searchParams = new URLSearchParams()
            querySite && site && searchParams.append('site', site)
            queryLocale && locale && searchParams.append('locale', locale)
            queryString = `?${searchParams.toString()}`
        }
        return `${sitePath}${localePath}${path}${queryString}`
    }
}

/*
 * Remove query params from a give url path based on a given list of keys
 *
 * @param {string} path - The part of url to have params removed from.
 * @param {array} keys - list of params to be removed
 * @returns {string} - the url after param has been removed
 * @example
 * import {removeQueryParamsFromPath} from /path/to/util/url
 *
 * removeQueryParamsFromPath(
 *   /en-GB/cart?pid=1234&color=black&size=s&abc=12,
 *   ['pid', 'color', 'size']
 * )
 * // returns
 * // '/en-GB/cart?abc=12'
 */
export const removeQueryParamsFromPath = (path, keys) => {
    const [pathname, search] = path.split('?')
    const params = new URLSearchParams(search)
    keys.forEach((key) => {
        if (params.has(key)) {
            params.delete(key)
        }
    })

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '')

    return `${pathname}${paramStr && '?'}${paramStr}`
}

/*
 * Remove site alias and locale from a given url, to be used for "navigate" urls
 *
 * @param {string} pathName - The part of url to have site alias and locale removed from
 * @returns {string} - the path after site alias and locale have been removed
 * @example
 * import {removeSiteLocaleFromPath} from /path/to/util/url
 *
 * removeSiteLocaleFromPath(/RefArch/en-US/account/wishlist)
 * // returns '/account/wishlist'
 */
export const removeSiteLocaleFromPath = (pathName = '') => {
    let {siteRef, localeRef} = getParamsFromPath(pathName)

    // remove the site alias from the current pathName
    if (siteRef) {
        pathName = pathName.replace(new RegExp(`/${siteRef}`, 'g'), '')
    }
    // remove the locale from the current pathName
    if (localeRef) {
        pathName = pathName.replace(new RegExp(`/${localeRef}`, 'g'), '')
    }

    return pathName
}

/**
 * Localize an href when it targets the current app origin.
 */
export const localizeSameDomainHref = (href, {buildUrl, currentOrigin}) => {
    if (!href || NON_NAVIGABLE_HREF_PATTERN.test(href) || !LOCALIZABLE_HREF_PATTERN.test(href)) {
        return href
    }

    let appOrigin
    try {
        appOrigin = new URL(currentOrigin).origin
    } catch {
        return href
    }

    let url
    try {
        url = new URL(href, currentOrigin)
    } catch {
        return href
    }

    if (url.origin !== appOrigin) {
        return href
    }

    const normalizedPath = removeSiteLocaleFromPath(url.pathname)
    const localizedPath = buildUrl(`${normalizedPath}${url.search}${url.hash}`)

    if (href.startsWith('/') && !href.startsWith('//')) {
        return localizedPath
    }

    if (href.startsWith('http://') || href.startsWith('https://')) {
        return `${url.origin}${localizedPath}`
    }

    if (href.startsWith('//')) {
        return `//${url.host}${localizedPath}`
    }

    return href
}

/**
 * Localize same-domain anchor hrefs inside SEO HTML content at render time.
 */
export const localizeSeoHtmlLinks = (html, {buildUrl, appOrigin}) => {
    if (!html || typeof html !== 'string') {
        return html
    }

    const currentOrigin = appOrigin || getAppOrigin()

    return html.replace(HTML_HREF_ATTR_PATTERN, (match, prefix, quote, href) => {
        const localizedHref = localizeSameDomainHref(href, {buildUrl, currentOrigin})
        return `${prefix}${quote}${localizedHref}${quote}`
    })
}
